import React, { useRef, useState } from 'react';
import { MoveHorizontal } from 'lucide-react';

/**
 * BeforeAfterSlider — interactive image comparison with a draggable divider.
 * before/after are image URLs. Optional label texts render as chips.
 */
const BeforeAfterSlider = ({
    before,
    after,
    beforeLabel = 'Before',
    afterLabel = 'After',
    aspect = 'aspect-[16/10]',
    testid = 'before-after',
}) => {
    const [pos, setPos] = useState(50); // percentage 0-100
    const containerRef = useRef(null);
    const dragging = useRef(false);

    const setFromClientX = (clientX) => {
        const el = containerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const raw = ((clientX - rect.left) / rect.width) * 100;
        setPos(Math.max(0, Math.min(100, raw)));
    };

    const onMouseMove = (e) => { if (dragging.current) setFromClientX(e.clientX); };
    const onTouchMove = (e) => { if (dragging.current && e.touches[0]) setFromClientX(e.touches[0].clientX); };
    const stop = () => { dragging.current = false; };

    return (
        <div
            ref={containerRef}
            className={`relative w-full ${aspect} overflow-hidden select-none cursor-ew-resize bg-neutral-100`}
            onMouseDown={(e) => { dragging.current = true; setFromClientX(e.clientX); }}
            onMouseMove={onMouseMove}
            onMouseUp={stop}
            onMouseLeave={stop}
            onTouchStart={(e) => { dragging.current = true; if (e.touches[0]) setFromClientX(e.touches[0].clientX); }}
            onTouchMove={onTouchMove}
            onTouchEnd={stop}
            data-testid={testid}
        >
            {/* After (bottom) */}
            <img
                src={after}
                alt={afterLabel}
                className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                draggable={false}
            />
            {/* Before (top, clipped) */}
            <div className="absolute inset-0 pointer-events-none" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
                <img
                    src={before}
                    alt={beforeLabel}
                    className="absolute inset-0 w-full h-full object-cover"
                    draggable={false}
                />
            </div>

            {/* Labels — use text-neutral-50 (not text-white) because the site's light-theme
                override remaps text-white to dark, which makes labels on dark backgrounds invisible. */}
            <div className="absolute top-4 left-4 label-caps text-xs bg-neutral-900 text-neutral-50 px-2.5 py-1 shadow-md">
                {beforeLabel}
            </div>
            <div className="absolute top-4 right-4 label-caps text-xs bg-brand-primary text-neutral-50 px-2.5 py-1 shadow-md">
                {afterLabel}
            </div>

            {/* Divider */}
            <div className="absolute top-0 bottom-0 w-0.5 bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.15)]" style={{ left: `${pos}%` }}>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white border-2 border-brand-primary flex items-center justify-center shadow-lg">
                    <MoveHorizontal className="h-5 w-5 text-brand-primary" />
                </div>
            </div>
        </div>
    );
};

export default BeforeAfterSlider;
